import { inject, Injectable, NgZone } from '@angular/core';
import {
  collection,
  doc,
  onSnapshot,
  query,
  Timestamp,
  where,
  type DocumentData,
  type QueryConstraint,
} from 'firebase/firestore';
import { combineLatest, map, Observable } from 'rxjs';
import { FIRESTORE } from '../core/firebase.tokens';
import type { EvidenceReference, Insight, InsightItem, MetricPoint } from './insight.model';

@Injectable({ providedIn: 'root' })
export class InsightFirestoreService {
  private readonly firestore = inject(FIRESTORE);
  private readonly zone = inject(NgZone);

  watchVisible(identity: string): Observable<Insight[]> {
    return combineLatest([
      this.watchBy('access_classification', '==', 'internal'),
      this.watchBy('viewer_identities', 'array-contains', identity),
    ]).pipe(
      map((groups) => {
        const insights = new Map<string, Insight>();
        groups.flat().forEach((insight) => insights.set(insight.id, insight));
        return [...insights.values()].sort(
          (a, b) => b.generatedAt.getTime() - a.generatedAt.getTime(),
        );
      }),
    );
  }

  watchOne(id: string): Observable<Insight | null> {
    return new Observable((subscriber) =>
      onSnapshot(
        doc(this.firestore, 'insight', id),
        (snapshot) =>
          this.zone.run(() =>
            subscriber.next(snapshot.exists() ? this.map(snapshot.id, snapshot.data()) : null),
          ),
        (error) => this.zone.run(() => subscriber.error(error)),
      ),
    );
  }

  private watchBy(
    field: string,
    operator: '==' | 'array-contains',
    value: string,
  ): Observable<Insight[]> {
    const constraints: QueryConstraint[] = [where(field, operator, value)];
    const source = query(collection(this.firestore, 'insight'), ...constraints);
    return new Observable((subscriber) =>
      onSnapshot(
        source,
        (snapshot) =>
          this.zone.run(() =>
            subscriber.next(snapshot.docs.map((item) => this.map(item.id, item.data()))),
          ),
        (error) => this.zone.run(() => subscriber.error(error)),
      ),
    );
  }

  private map(id: string, data: DocumentData): Insight {
    return {
      id,
      type: data['type'] ?? '',
      category: data['category'] ?? '',
      cadence: data['cadence'] ?? '',
      policyVersion: data['policy_version'] ?? '',
      status: data['status'] ?? '',
      title: data['title'] ?? '',
      summary: data['summary'] ?? '',
      periodStartAt: this.date(data['period_start_at']) ?? new Date(0),
      periodEndAt: this.date(data['period_end_at']) ?? new Date(0),
      generatedAt: this.date(data['generated_at']) ?? new Date(0),
      itemCount: Number(data['item_count'] ?? 0),
      items: (data['items'] ?? []).map((item: DocumentData): InsightItem => this.item(item)),
      accessClassification: data['access_classification'] ?? 'internal',
      viewerIdentities: data['viewer_identities'] ?? [],
      createdByIdentity: data['created_by_identity'] ?? '',
      createdAt: this.date(data['created_at']) ?? new Date(0),
      updatedAt: this.date(data['updated_at']) ?? new Date(0),
    };
  }

  private item(data: DocumentData): InsightItem {
    return {
      type: data['type'] ?? '',
      title: data['title'] ?? '',
      explanation: data['explanation'] ?? '',
      method: data['method'] ?? '',
      confidence: this.number(data['confidence']),
      importance: data['importance'] ?? '',
      scopeType: data['scope_type'] ?? null,
      scopeId: data['scope_id'] ?? null,
      scopeName: data['scope_name'] ?? null,
      metricName: data['metric_name'] ?? null,
      unit: data['unit'] ?? null,
      baselineValue: this.number(data['baseline_value']),
      currentValue: this.number(data['current_value']),
      absoluteChange: this.number(data['absolute_change']),
      relativeChangePercentage: this.number(data['relative_change_percentage']),
      thresholdPercentage: this.number(data['threshold_percentage']),
      direction: data['direction'] ?? null,
      baselinePoints: (data['baseline_points'] ?? []).map((point: DocumentData): MetricPoint => ({
        periodStartAt: this.date(point['period_start_at']) ?? new Date(0),
        periodEndAt: this.date(point['period_end_at']) ?? new Date(0),
        value: Number(point['value'] ?? 0),
      })),
      dataQualityStatus: data['data_quality_status'] ?? 'unknown',
      evidence: (data['evidence'] ?? []).map((evidence: DocumentData): EvidenceReference => ({
        sourceType: evidence['source_type'] ?? '',
        sourceId: evidence['source_id'] ?? '',
        label: evidence['label'] ?? null,
        link: evidence['link'] ?? null,
      })),
      attributes: data['attributes'] ?? {},
      itemId: data['item_id'] ?? null,
      ownerIdentity: data['owner_identity'] ?? null,
      ownerTeam: data['owner_team'] ?? null,
      recommendedAction: data['recommended_action'] ?? null,
      expectedBenefit: data['expected_benefit'] ?? null,
      reviewStatus: data['review_status'] ?? null,
      reviewerIdentity: data['reviewer_identity'] ?? null,
      reviewedAt: this.date(data['reviewed_at']),
      reviewNote: data['review_note'] ?? null,
      followUpStatus: data['follow_up_status'] ?? null,
      followUpReference: data['follow_up_reference'] ?? null,
    };
  }

  private number(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  private date(value: unknown): Date | null {
    if (value instanceof Timestamp) return value.toDate();
    if (value instanceof Date) return value;
    return null;
  }
}
